import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type TourStep = 1 | 2 | 3 | 4;

interface TourContextType {
  active: boolean;
  step: TourStep;
  startTour: () => void;
  setStep: (step: TourStep) => void;
  dismissTour: () => void;
  completeTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const STORAGE_KEY = 'verithos_tour_state';
const DISMISSED_KEY = 'verithos_tour_dismissed';

interface StoredState {
  active: boolean;
  step: TourStep;
}

function readStored(): StoredState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.active === 'boolean' && [1, 2, 3, 4].includes(parsed?.step)) {
      return parsed as StoredState;
    }
  } catch { /* ignore */ }
  return null;
}

function writeStored(state: StoredState | null) {
  try {
    if (state === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const initial = readStored();
  const [active, setActive] = useState<boolean>(initial?.active ?? false);
  const [step, setStepState] = useState<TourStep>(initial?.step ?? 1);

  // Auto-start when ?tour=1 appears in URL (and not previously dismissed in this session)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tour') === '1') {
      let dismissed = false;
      try { dismissed = sessionStorage.getItem(DISMISSED_KEY) === '1'; } catch { /* ignore */ }
      if (!dismissed && !active) {
        setActive(true);
        setStepState(1);
      }
    }
  }, [location.search, active]);

  // Persist
  useEffect(() => {
    if (active) writeStored({ active, step });
    else writeStored(null);
  }, [active, step]);

  const startTour = useCallback(() => {
    try { sessionStorage.removeItem(DISMISSED_KEY); } catch { /* ignore */ }
    setActive(true);
    setStepState(1);
  }, []);

  const setStep = useCallback((s: TourStep) => setStepState(s), []);

  const markResolved = () => {
    try {
      localStorage.setItem('verithos:tour_resolved_at', String(Date.now()));
      // Mark "resolved this session" so nudges can defer to a return session.
      sessionStorage.setItem('verithos:tour_resolved_this_session', '1');
    } catch { /* ignore */ }
  };

  const dismissTour = useCallback(() => {
    setActive(false);
    setStepState(1);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
    markResolved();
    writeStored(null);
  }, []);

  const completeTour = useCallback(() => {
    setActive(false);
    setStepState(1);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
    markResolved();
    writeStored(null);
  }, []);

  return (
    <TourContext.Provider value={{ active, step, startTour, setStep, dismissTour, completeTour }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}
